import React, { useState, useEffect, useCallback } from "react";
import Footer from "../footer";
import Navbar from "../Navbar";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import SignOut from "../SignOut";
import axios from "axios";
import Auth from "../../Auth";
import { api, link } from "../link.js";
import { hasPermissions, hasPermissionsStrict } from "../../security/permissions";
import SettingsTabs from "./SettingsTabs";
import Switch from "@mui/material/Switch";
import "./EmailSettings.css";

const emailUrl = () => `${api()}api/email-configs`;

const Profile = () => {
  const [emails, setEmails] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [communicationGroups, setCommunicationGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const canManageEmails = hasPermissions("MANAGE_EMAILS");
  const canEditEmails = hasPermissionsStrict("SYSTEM_ADMIN");
  const canViewTasks = hasPermissions("VIEW_TASKS");
  const canManageTasks = hasPermissions("MANAGE_TASKS");

  const reportError = useCallback((failure, message) => {
    Auth.handleHttpCodes(failure, "Profile");
    setError(message);
  }, []);
  const getEmails = useCallback(() => axios.get(emailUrl())
    .then(({ data }) => setEmails(Array.isArray(data) ? data : [])), []);

  useEffect(() => {
    if (canManageEmails) {
      getEmails().catch((failure) => reportError(failure, "Email settings could not be loaded."));
    }
    if (canViewTasks) {
      axios.post(link() + "&do=get&ra=task&name=&taskID", {}, {
        headers: { "Content-Type": "application/json", Authorization: localStorage.getItem("token") || "" },
        timeout: 30000,
      }).then(({ data }) => setTasks(Array.isArray(data) ? data : []))
        .catch((failure) => reportError(failure, "Task settings could not be loaded."));
    }
  }, [canManageEmails, canViewTasks, getEmails, reportError]);

  const toggleEmail = async (item) => {
    if (!hasPermissions("MANAGE_EMAILS") || busy) return;
    const active = item.emailStatus !== "Active";
    setBusy(true);
    setError("");
    try {
      await axios.patch(`${emailUrl()}/${encodeURIComponent(item.emailID)}/status`, { active });
      await getEmails().catch((failure) => reportError(failure, "Email settings could not be refreshed."));
    } catch (failure) {
      reportError(failure, "Email status could not be updated.");
    } finally {
      setBusy(false);
    }
  };

  const openEditor = async (item) => {
    if (!hasPermissionsStrict("SYSTEM_ADMIN") || busy) return;
    setBusy(true);
    setError("");
    setModalError("");
    try {
      const [{ data }, { data: groups }] = await Promise.all([
        axios.get(`${emailUrl()}/${encodeURIComponent(item.emailID)}`),
        axios.get(`${api()}api/security/groups`),
      ]);
      if (!Array.isArray(groups)) throw new Error("Invalid communication group list");
      const options = new Map();
      groups.forEach((group) => {
        const tag = (group.tag || "").trim();
        if (!tag) return;
        const name = group.name || group.otherName || tag;
        options.set(tag, options.has(tag) ? `${options.get(tag)}, ${name}` : name);
      });
      (data.communicationGroups || "").split(",").map((tag) => tag.trim()).filter(Boolean).forEach((tag) => {
        if (!options.has(tag)) options.set(tag, `Unavailable group (${tag})`);
      });
      setCommunicationGroups(Array.from(options, ([tag, name]) => ({ tag, name })));
      setEditing({
        emailID: item.emailID,
        username: data.username || "", names: data.names || "", host: data.host || "",
        port: data.port, timeout: data.timeout, smtpAuth: Boolean(data.smtpAuth),
        starttls: Boolean(data.starttls), password: "",
        communicationGroups: data.communicationGroups || "",
      });
    } catch (failure) {
      reportError(failure, "Email configuration could not be loaded.");
    } finally {
      setBusy(false);
    }
  };

  const saveEmail = async (event) => {
    event.preventDefault();
    if (!hasPermissionsStrict("SYSTEM_ADMIN") || !editing || busy) return;
    setBusy(true);
    setModalError("");
    const { emailID, ...fields } = editing;
    try {
      await axios.put(`${emailUrl()}/${encodeURIComponent(emailID)}`, {
        ...fields, port: Number(fields.port), timeout: Number(fields.timeout),
      });
      setEditing(null);
      setError("");
      await getEmails().catch((failure) => reportError(failure, "Email settings could not be refreshed."));
    } catch (failure) {
      Auth.handleHttpCodes(failure, "Profile");
      setModalError("Email configuration could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const toggleTask = async (item) => {
    if (!hasPermissions("MANAGE_TASKS") || busy) return;
    const taskStatus = item.taskStatus === "Active" ? "Inactive" : "Active";
    setBusy(true);
    setError("");
    try {
      await axios.post(link() + `&do=status&ra=task&name=${encodeURIComponent(item.name)}&taskStatus=${taskStatus}`,
        { ...item, taskStatus }, {
          headers: { "Content-Type": "application/json", Authorization: localStorage.getItem("token") || "" },
        });
      setTasks((current) => current.map((task) => task.taskID === item.taskID ? { ...task, taskStatus } : task));
    } catch (failure) {
      reportError(failure, "Task status could not be updated.");
    } finally {
      setBusy(false);
    }
  };

  const changeField = (event) => {
    const { name, type, checked, value } = event.target;
    setEditing((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  return (
    <div>
      <div className="contentdashbord">
        <Navbar />
        <SignOut />
        <br />
        <h6 className="activejobs">System Settings</h6>
        <SettingsTabs />
        {error && <Alert variant="danger">{error}</Alert>}
        {canViewTasks && (
          <div className="cardtable mt-3">
            <div className="containeruserscroll">
              <div className="membadd d-flex"><p className="contentmemb">Task Settings</p></div>
              <table className="table table-hover table-responsive tablealot">
                <thead><tr><th className="userpronyblue">Message</th><th className="userpronyblue">Enable</th></tr></thead>
                <tbody>
                  {tasks.map((item) => (
                    <tr key={item.taskID}>
                      <td className="userinfowe">{item.message}</td>
                      <td className="userinfowe">
                        <Switch checked={item.taskStatus === "Active"} disabled={!canManageTasks || busy}
                          onChange={() => toggleTask(item)} color="primary"
                          inputProps={{ "aria-label": `Enable task ${item.message}` }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {canManageEmails && (
          <div className="cardtable mt-3">
            <div className="containeruserscroll">
              <div className="membadd d-flex"><p className="contentmemb">Email Settings</p></div>
              <table className="table table-hover table-responsive tablealot">
                <thead><tr><th className="userpronyblue">Email</th><th className="userpronyblue">Title</th>
                  <th className="userpronyblue">Enable</th>{canEditEmails && <th className="userpronyblue">Edit</th>}</tr></thead>
                <tbody>
                  {emails.map((item) => (
                    <tr key={item.emailID}>
                      <td className="userinfowe">{item.email}</td>
                      <td className="userinfowe">{item.title}</td>
                      <td className="userinfowe">
                        <Switch checked={item.emailStatus === "Active"} disabled={busy}
                          onChange={() => toggleEmail(item)} color="primary"
                          inputProps={{ "aria-label": `Enable email ${item.email}` }} />
                      </td>
                      {canEditEmails && <td className="userinfowe">
                        <Button disabled={busy} onClick={() => openEditor(item)} aria-label={`Edit email ${item.email}`}>Edit</Button>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!emails.length && <p>No email configurations found.</p>}
            </div>
          </div>
        )}
        {canEditEmails && <Modal className="email-settings-modal" show={Boolean(editing)} onHide={() => !busy && setEditing(null)}
          aria-labelledby="email-settings-title" backdrop={busy ? "static" : true} keyboard={!busy}>
          <Modal.Header closeButton={!busy}><Modal.Title id="email-settings-title">Edit email configuration</Modal.Title></Modal.Header>
          {editing && <Form onSubmit={saveEmail}>
            <Modal.Body>
              {modalError && <Alert variant="danger">{modalError}</Alert>}
              {[["username", "Username"], ["names", "Sender name"], ["host", "SMTP host"],
                ["port", "SMTP port"], ["timeout", "Timeout"]].map(([name, label]) => (
                <Form.Group controlId={`email-${name}`} key={name}>
                  <Form.Label>{label}</Form.Label>
                  <Form.Control name={name} value={editing[name] === undefined ? "" : editing[name]}
                    onChange={changeField} disabled={busy} required={name !== "names"}
                    type={["port", "timeout"].includes(name) ? "number" : "text"}
                    min={name === "port" ? 1 : name === "timeout" ? 0 : undefined}
                    max={name === "port" ? 65535 : undefined} />
                </Form.Group>
              ))}
              <Form.Check id="email-smtpAuth" name="smtpAuth" label="SMTP authentication" checked={editing.smtpAuth}
                onChange={changeField} disabled={busy} />
              <Form.Check id="email-starttls" name="starttls" label="STARTTLS" checked={editing.starttls}
                onChange={changeField} disabled={busy} />
              <Form.Group controlId="email-communicationGroups">
                <Form.Label>Communication groups</Form.Label>
                <Form.Control as="select" multiple name="communicationGroups"
                  value={editing.communicationGroups ? editing.communicationGroups.split(",").map((tag) => tag.trim()).filter(Boolean) : [""]}
                  onChange={(event) => {
                    const selected = Array.from(event.target.selectedOptions, (option) => option.value);
                    setEditing((current) => ({ ...current,
                      communicationGroups: selected.includes("") && current.communicationGroups
                        ? "" : selected.filter(Boolean).join(","),
                    }));
                  }} disabled={busy} aria-describedby="email-communicationGroups-help">
                  <option value="">All eligible members</option>
                  {communicationGroups.map((group) => <option key={group.tag} value={group.tag}>{group.name}</option>)}
                </Form.Control>
                <Form.Text id="email-communicationGroups-help">
                  Select communication groups, or All eligible members for no group restriction.
                </Form.Text>
              </Form.Group>
              <Form.Group controlId="email-password">
                <Form.Label>Password</Form.Label>
                <Form.Control type="password" name="password" autoComplete="new-password" value={editing.password}
                  onChange={changeField} disabled={busy} aria-describedby="email-password-help" />
                <Form.Text id="email-password-help">Leave blank to keep the stored password.</Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" disabled={busy} onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" disabled={busy}>Save</Button>
            </Modal.Footer>
          </Form>}
        </Modal>}
      </div>
      <br />
      <Footer />
    </div>
  );
};

export default Profile;
